import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import type {
  ResourceJsonConfigResponse,
  ResourceJsonErrorCode,
} from '@/features/resource/configuration/endpoints/resourceJsonConfiguration'
import {
  GET_RESOURCE_JSON_API,
  getResourceJson,
} from '@/features/resource/configuration/endpoints/resourceJsonConfiguration'
import { extractResourceJsonErrorCode } from '@/features/resource/configuration/utils/resourceJsonErrorCodes'
import type { UseQueryOptions } from '@/types/queries'

export type UseResourceJsonQueryResult = Omit<UseQueryResult, 'data'> & {
  resourceJsonConfig: ResourceJsonConfigResponse | null
  /** Typed business-error code parsed from a 422 GET rejection, or null (network/5xx/unknown). */
  errorCode: ResourceJsonErrorCode | null
}

/** Errors carrying a known business code (422 NOT_BATTERY / CONTROLLER_NOT_FOUND / …) are not
 * transient — retrying just keeps the page spinner up for the whole backoff window. Skip retries for
 * those; keep the default 3 attempts for genuinely transient failures (network, 5xx). */
export function shouldRetryResourceJson(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (status === 422) {
      // Recognised business error → never retry. Unrecognised 422 → at most one retry.
      return extractResourceJsonErrorCode(error.response?.data) ? false : failureCount < 1
    }
    // Other 4xx (auth, bad request) are terminal — retrying only delays the error.
    if (status !== undefined && status >= 400 && status < 500) {
      return false
    }
  }
  // 5xx and network errors are transient — keep the default attempts.
  return failureCount < 3
}

export function useResourceJsonQuery(
  resourceId: string,
  options?: UseQueryOptions<ResourceJsonConfigResponse | null>,
): UseResourceJsonQueryResult {
  const { data, ...queryResult } = useQuery<ResourceJsonConfigResponse | null>({
    ...options,
    retry: shouldRetryResourceJson,
    queryFn: () => getResourceJson(resourceId),
    queryKey: [GET_RESOURCE_JSON_API, resourceId],
  })

  // No useErrorHandler toast here — the inline ResourceJsonErrorAlert (for known business codes) and
  // the generic load_error message (for network/5xx/unknown) are the dedicated surface for this
  // query's failure path. The hook is also called from two places in the page tree (Provider +
  // Content), so a generic toast here would double-fire.

  const errorCode = axios.isAxiosError(queryResult.error)
    ? extractResourceJsonErrorCode(queryResult.error.response?.data)
    : null

  return {
    ...queryResult,
    resourceJsonConfig: data ?? null,
    errorCode,
  }
}

export function useInvalidateResourceJsonQuery() {
  const queryClient = useQueryClient()

  return (resourceId: string) =>
    queryClient.invalidateQueries({
      queryKey: [GET_RESOURCE_JSON_API, resourceId],
    })
}
